import { createIcons, ArrowLeft, ArrowRight, Save, Plus, Trash2, CheckCircle2, AlertTriangle, XCircle, FileText, Camera, RefreshCw, Sparkles, ShieldCheck } from "lucide";
import { router } from "../router/router.js";
import { contextStore } from "../auth/context-store.js";
import { sessionRequestContext } from "../auth/request-context.js";
import { domainId, type EquipmentId, type InspectionId, type ChecklistId, type ChecklistComponentId, type ProfessionalId } from "../../shared/kernel/identifiers.js";
import { determineAvrCriticality, type AvrCriticality } from "../../domain/engineering/avr-domain.js";
import type { RecommendationCriticality, RecommendationExecuteIn } from "../../domain/recommendation/recommendation-domain.js";

interface ChecklistItemState {
  groupId: string;
  definitionComponentId: string;
  name: string;
  result: 'APPROVED' | 'REJECTED' | 'NOT_APPLICABLE' | null;
  description: string;
  inspectionNotes: string;
  photos: string[];
}

interface NdtItemState {
  id: string;
  type: 'ME' | 'LP' | 'PM' | 'B_SCAN';
  component: string;
  standard: string;
  result: 'APPROVED' | 'REJECTED';
  findings: string;
  value?: number | undefined;
  assetSnapshotIds?: string[];
  assetNames?: string[];
}

interface RecItemState {
  id: string;
  discipline: string;
  criticality: string;
  executeIn: string;
  scope: string;
  zr?: string;
  origin?: string;
}

export class AppInspectionFlow extends HTMLElement {
  private escapeHtml(str: any): string {
    if (str === null || str === undefined) return '';
    return String(str).replace(/"/g, '&quot;').replace(/'/g, '&#39;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  private currentStepIndex: number = 0;
  private equipments: any[] = [];
  
  // Inspection Session State
  private selectedEquipmentId: string = "";
  private inspectionType: string = "EXTERNAL";
  private modality: 'INTERNAL' | 'EXTERNAL' | 'GENERAL' = 'EXTERNAL';
  private nature: 'INITIAL' | 'PERIODIC' | 'EXTRAORDINARY' = 'PERIODIC';
  private extraordinaryReason: string = '';
  private inspectorId: string = contextStore.get().userName;
  private professionals: Array<{ id: string; name: string; functionTitle: string }> = [];
  private notes: string = '';
  
  private activeInspectionId: InspectionId | null = null;
  private activeChecklistId: ChecklistId | null = null;
  
  // Steps Data
  private checklistItems: ChecklistItemState[] = [
    { groupId: "CASCO", definitionComponentId: "DEF-01", name: "Costado / Chapa do Casco", result: 'APPROVED', description: "", inspectionNotes: "", photos: [] },
    { groupId: "TAMPOS", definitionComponentId: "DEF-02", name: "Tampo Superior / Cabeçote", result: 'APPROVED', description: "", inspectionNotes: "", photos: [] },
    { groupId: "TAMPOS", definitionComponentId: "DEF-03", name: "Tampo Inferior", result: 'APPROVED', description: "", inspectionNotes: "", photos: [] },
    { groupId: "BOCAIS", definitionComponentId: "DEF-04", name: "Bocal de Entrada de Produto", result: 'APPROVED', description: "", inspectionNotes: "", photos: [] },
    { groupId: "BOCAIS", definitionComponentId: "DEF-05", name: "Bocal de Saída de Produto", result: 'APPROVED', description: "", inspectionNotes: "", photos: [] },
    { groupId: "VALVULAS", definitionComponentId: "DEF-06", name: "Válvula de Segurança", result: 'APPROVED', description: "", inspectionNotes: "", photos: [] },
    { groupId: "SUPORTES", definitionComponentId: "DEF-07", name: "Berço / Sapatas de Apoio", result: 'APPROVED', description: "", inspectionNotes: "", photos: [] },
    { groupId: "PINTURA", definitionComponentId: "DEF-08", name: "Revestimento / Pintura", result: 'APPROVED', description: "", inspectionNotes: "", photos: [] },
    { groupId: "INSTRUMENTOS", definitionComponentId: "DEF-09", name: "Manômetro / Instrumentos", result: 'APPROVED', description: "", inspectionNotes: "", photos: [] }
  ];

  private ndtRecords: NdtItemState[] = [];

  private avrData = {
    previousThickness: 0,
    currentThickness: 0,
    minimumThickness: 0,
    intervalYears: 0
  };

  private recommendations: RecItemState[] = [];

  private steps = [
    { id: 'step-equipment', name: '1. Identificação' },
    { id: 'step-checklist', name: '2. Checklist Visual' },
    { id: 'step-end', name: '3. Ensaios END' },
    { id: 'step-avr', name: '4. Cálculo AVR' },
    { id: 'step-recommendations', name: '5. Recomendações' },
    { id: 'step-ri', name: '6. Emissão RI' }
  ];

  async connectedCallback() {
    await this.loadEquipments();
    this.render();
  }

  private async loadEquipments() {
    try {
      const p = window.app.platform;
      const ctx = sessionRequestContext();
      this.equipments = (await p.equipmentService.listEquipments(ctx)) as any[];
      if (this.equipments.length > 0 && !this.selectedEquipmentId) {
        this.selectedEquipmentId = "";
      }
      // FRENTE B — INSPETOR GLOBAL: usar listEligibleInspectors (fonte oficial
      // do Controle de Campo) em vez de listProfessionals que filtra por
      // activeClientId. Inspetores são PROFESSIONALS GLOBAIS e devem aparecer
      // independentemente do cliente/projeto selecionado no formulário.
      if (p.fieldControlService) {
        const eligible = await p.fieldControlService.listEligibleInspectors(ctx).catch(() => []);
        this.professionals = eligible.map(pr => ({ id: pr.id, name: pr.name, functionTitle: pr.functionTitle || 'INSPETOR DE EQUIPAMENTOS' }));
        if (this.professionals.length > 0 && (!this.inspectorId || this.inspectorId === contextStore.get().userName)) {
          this.inspectorId = this.professionals[0]?.id || "";
        }
      } else if (p.adminService) {
        // Fallback defensivo caso fieldControlService não esteja disponível
        const profs = await p.adminService.listProfessionals(ctx).catch(() => []);
        this.professionals = profs.map(pr => ({ id: pr.id, name: pr.name, functionTitle: pr.functionTitle || 'INSPETOR DE EQUIPAMENTOS' }));
        if (this.professionals.length > 0 && (!this.inspectorId || this.inspectorId === contextStore.get().userName)) {
          this.inspectorId = this.professionals[0]?.id || "";
        }
      }
    } catch (e) {
      console.warn("Could not load equipment list", e);
    }
  }

  private render() {
    this.innerHTML = `
      <style>
        .flow-shell {
          display: flex;
          flex-direction: column;
          gap: 20px;
          max-width: 1200px;
          margin: 0 auto;
        }
        .flow-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-bottom: 16px;
          border-bottom: 1px solid var(--color-border-default);
        }
        .flow-steps-bar {
          display: flex;
          gap: 8px;
          overflow-x: auto;
          padding-bottom: 4px;
        }
        .flow-step-pill {
          padding: 8px 16px;
          border-radius: 6px;
          background: var(--color-surface-secondary);
          border: 1px solid var(--color-border-default);
          color: var(--color-text-secondary);
          font-size: 0.85rem;
          font-weight: 500;
          cursor: pointer;
          white-space: nowrap;
          transition: all 0.2s ease;
        }
        .flow-step-pill:hover {
          border-color: var(--color-primary);
        }
        .flow-step-pill.active {
          background: var(--color-primary);
          color: var(--color-text-primary);
          border-color: var(--color-primary);
          font-weight: 600;
          box-shadow: 0 2px 8px rgba(0,0,0,0.2);
        }
        .flow-card {
          background: var(--color-surface-primary);
          border: 1px solid var(--color-border-default);
          border-radius: 8px;
          padding: 24px;
          min-height: 420px;
        }
        .flow-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 0;
          border-top: 1px solid var(--color-border-default);
        }
        .form-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 16px;
        }
        .form-group {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .form-label {
          font-size: 0.85rem;
          font-weight: 500;
          color: var(--color-text-secondary);
        }
        .form-control {
          background: var(--color-background);
          border: 1px solid var(--color-border-default);
          color: var(--color-text-primary);
          padding: 10px 12px;
          border-radius: 6px;
          font-family: inherit;
          font-size: 0.9rem;
        }
        .form-control:focus {
          outline: none;
          border-color: var(--color-primary);
        }
        .checklist-row {
          background: rgba(255, 255, 255, 0.02);
          border: 1px solid var(--ia-line, rgba(255, 255, 255, 0.05));
          border-radius: 10px;
          padding: 16px 20px;
          margin-bottom: 14px;
          display: flex;
          flex-direction: column;
          gap: 12px;
          transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
          position: relative;
          overflow: hidden;
        }
        .checklist-row:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
          border-color: rgba(96, 165, 250, 0.3);
          background: rgba(255, 255, 255, 0.04);
        }
        .checklist-row::before {
          content: '';
          position: absolute;
          top: 0; left: 0; width: 4px; height: 100%;
          background: var(--color-border-default);
          transition: background 0.3s ease;
        }
        .checklist-row:has(.active-approved)::before { background: var(--status-success-text); }
        .checklist-row:has(.active-rejected)::before { background: var(--status-danger-solid); }
        .checklist-row:has(.active-na)::before { background: var(--color-graphite-400); }
        
        .checklist-header-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 12px;
        }
        .pill-group {
          display: flex;
          gap: 8px;
          background: rgba(0, 0, 0, 0.2);
          padding: 4px;
          border-radius: 8px;
          border: 1px solid var(--ia-line, rgba(255, 255, 255, 0.05));
        }
        .result-btn {
          border: 1px solid transparent;
          padding: 8px 16px;
          border-radius: 6px;
          font-size: 0.75rem;
          font-weight: 700;
          cursor: pointer;
          background: transparent;
          color: var(--color-text-muted);
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .result-btn:hover {
          background: rgba(255, 255, 255, 0.1);
          color: var(--color-text-primary);
        }
        .result-btn.active-approved {
          background: rgba(34, 197, 94, 0.15);
          color: var(--status-success-text);
          border-color: rgba(34, 197, 94, 0.4);
          box-shadow: 0 0 12px rgba(34, 197, 94, 0.1);
        }
        .result-btn.active-rejected {
          background: rgba(239, 68, 68, 0.15);
          color: var(--status-danger-solid);
          border-color: rgba(239, 68, 68, 0.4);
          box-shadow: 0 0 12px rgba(239, 68, 68, 0.1);
        }
        .result-btn.active-na {
          background: rgba(148, 163, 184, 0.15);
          color: var(--color-graphite-300);
          border-color: rgba(148, 163, 184, 0.4);
        }
        .defect-box {
          background: linear-gradient(145deg, rgba(239, 68, 68, 0.05) 0%, rgba(239, 68, 68, 0.02) 100%);
          border: 1px solid rgba(239, 68, 68, 0.2);
          border-left: 3px solid var(--status-danger-solid);
          border-radius: 6px;
          padding: 16px;
          margin-top: 12px;
          display: flex;
          flex-direction: column;
          gap: 12px;
          box-shadow: inset 0 2px 4px rgba(0,0,0,0.1);
        }
        .defect-box textarea, .defect-box input {
          background: rgba(0,0,0,0.2) !important;
          border-color: rgba(239,68,68,0.2) !important;
        }
        .defect-box textarea:focus, .defect-box input:focus {
          border-color: var(--status-danger-solid) !important;
          box-shadow: 0 0 0 2px rgba(239,68,68,0.2) !important;
        }
        .kpi-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 16px;
          margin-top: 16px;
        }
        .kpi-box {
          background: var(--color-background);
          border: 1px solid var(--color-border-default);
          border-radius: 8px;
          padding: 16px;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .kpi-title {
          font-size: 0.75rem;
          color: var(--color-text-muted);
          text-transform: uppercase;
          font-weight: 600;
        }
        .kpi-val {
          font-size: 1.6rem;
          font-weight: 700;
          color: var(--color-text-primary);
        }
        .kpi-unit {
          font-size: 0.8rem;
          color: var(--color-text-muted);
        }
      </style>

      <div class="flow-shell">
        <div class="flow-header">
          <div>
            <h1 style="color: var(--color-text-primary); margin: 0; font-size: 1.5rem; font-weight: 600;">Fluxo Operacional de Inspeção NR-13</h1>
            <p style="color: var(--color-text-muted); margin: 4px 0 0 0; font-size: 0.875rem;">
              Execução passo a passo: Cadastro, Checklist Visual, END, Cálculo AVR e Consolidação no RI.
            </p>
          </div>
          <app-button variant="secondary" size="sm" id="btn-back-inspections">
            <i data-lucide="arrow-left" style="width: 14px; height: 14px; margin-right: 4px;"></i> Voltar para Lista
          </app-button>
        </div>

        <div class="flow-steps-bar">
          ${this.steps.map((s, idx) => `
            <div class="flow-step-pill ${this.currentStepIndex === idx ? 'active' : ''}" data-step-idx="${idx}">
              ${s.name}
            </div>
          `).join('')}
        </div>

        <div class="flow-card">
          ${this.renderStepContent()}
        </div>

        <div class="flow-footer">
          <app-button variant="secondary" size="sm" id="btn-prev-step" ${this.currentStepIndex === 0 ? 'style="visibility:hidden;"' : ''}>
            <i data-lucide="arrow-left" style="width: 14px; height: 14px; margin-right: 4px;"></i> Anterior
          </app-button>
          
          <div style="display: flex; gap: 8px;">
            <app-button variant="secondary" size="sm" id="btn-save-draft">
              <i data-lucide="save" style="width: 14px; height: 14px; margin-right: 4px;"></i> Salvar Dados
            </app-button>
            ${this.currentStepIndex === this.steps.length - 1 ? `
              <app-button variant="primary" size="sm" id="btn-finish-ri">
                <i data-lucide="sparkles" style="width: 14px; height: 14px; margin-right: 4px;"></i> Gerar e Abrir RI
              </app-button>
            ` : `
              <app-button variant="primary" size="sm" id="btn-next-step">
                Próximo <i data-lucide="arrow-right" style="width: 14px; height: 14px; margin-left: 4px;"></i>
              </app-button>
            `}
          </div>
        </div>
      </div>
    `;

    createIcons({
      icons: { ArrowLeft, ArrowRight, Save, Plus, Trash2, CheckCircle2, AlertTriangle, XCircle, FileText, Camera, RefreshCw, Sparkles, ShieldCheck },
      nameAttr: "data-lucide",
      root: this
    });

    this.attachEvents();
  }

  private renderStepContent(): string {
    switch (this.currentStepIndex) {
      case 0:
        return this.renderStepEquipment();
      case 1:
        return this.renderStepChecklist();
      case 2:
        return this.renderStepNdt();
      case 3:
        return this.renderStepAvr();
      case 4:
        return this.renderStepRecommendations();
      case 5:
        return this.renderStepRiSummary();
      default:
        return `<p>Etapa não encontrada.</p>`;
    }
  }

  private renderStepEquipment(): string {
    return `
      <div>
        <h3 style="color: var(--color-text-primary); margin-top: 0; margin-bottom: 8px;">1. Identificação do Equipamento e Escopo</h3>
        <p style="color: var(--color-text-muted); font-size: 0.875rem; margin-bottom: 24px;">
          Selecione o equipamento cadastrado ou informe a TAG, modalidade e natureza da inspeção conforme diretrizes da NR-13.
        </p>

        <div class="form-grid">
          <div class="form-group">
            <label class="form-label">Equipamento (TAG / Nome) *</label>
            <select class="form-control" id="inp-eq-select">
              <option value="" disabled selected>SELECIONAR EQUIPAMENTO...</option>
              ${this.equipments.length === 0 ? '<option value="" disabled>Nenhum equipamento cadastrado</option>' : ''}
              ${this.equipments.map(eq => `
                <option value="${eq.id}" ${eq.id === this.selectedEquipmentId ? 'selected' : ''}>
                  ${eq.tag} - ${eq.name || eq.description || 'Equipamento'} (${eq.service || 'Serviço Operacional'})
                </option>
              `).join('')}
            </select>
          </div>

          <div class="form-group">
            <label class="form-label">Tipo de Inspeção *</label>
            <select class="form-control" id="inp-insp-type">
              <option value="EXTERNAL" ${this.inspectionType === 'EXTERNAL' ? 'selected' : ''}>Externa (Em Operação)</option>
              <option value="INTERNAL" ${this.inspectionType === 'INTERNAL' ? 'selected' : ''}>Interna (Parada / Fora de Operação)</option>
              <option value="GENERAL" ${this.inspectionType === 'GENERAL' ? 'selected' : ''}>Geral (Interna + Externa)</option>
            </select>
          </div>

          <div class="form-group">
            <label class="form-label">Natureza da Inspeção *</label>
            <select class="form-control" id="inp-nature">
              <option value="PERIODIC" ${this.nature === 'PERIODIC' ? 'selected' : ''}>Periódica (Ordinária NR-13)</option>
              <option value="INITIAL" ${this.nature === 'INITIAL' ? 'selected' : ''}>Inicial (Prévia à entrada em operação)</option>
              <option value="EXTRAORDINARY" ${this.nature === 'EXTRAORDINARY' ? 'selected' : ''}>Extraordinária (Reparo / Incidente)</option>
            </select>
          </div>

          <div class="form-group" id="group-extraordinary-reason" style="${this.nature === 'EXTRAORDINARY' ? '' : 'display:none;'}">
            <label class="form-label">Motivo da Inspeção Extraordinária *</label>
            <input class="form-control" id="inp-extra-reason" placeholder="Ex: Queda de pressão ou reparo estrutural" value="${this.escapeHtml(this.extraordinaryReason)}">
          </div>

          <div class="form-group">
            <label class="form-label" style="display:flex; justify-content:space-between; align-items:center;">
              <span>Inspetor Responsável *</span>
              <span style="font-size:0.75rem; color: var(--status-success-text); font-weight:600;">🔒 Função Conforme Cadastro (Não Editável)</span>
            </label>
            <select class="form-control" id="inp-inspector" style="margin-bottom:6px;">
              ${this.professionals.map(p => `
                <option value="${p.id}" data-function="${p.functionTitle}" ${this.inspectorId === p.id ? 'selected' : ''}>
                  ${p.name} (${p.functionTitle})
                </option>
              `).join('')}
              ${this.professionals.length === 0 ? `<option value="${this.inspectorId}">${this.inspectorId}</option>` : ''}
            </select>
            <input class="form-control" id="inp-inspector-function" value="${this.professionals.find(p => p.id === this.inspectorId)?.functionTitle || 'INSPETOR DE EQUIPAMENTOS'}" readonly style="background:rgba(255,255,255,0.05); color:var(--color-graphite-400); cursor:not-allowed;" title="A função do colaborador é preenchida automaticamente pelo cadastro e não pode ser editada.">
          </div>

          <div class="form-group" style="grid-column: 1 / -1;">
            <label class="form-label">Observações Gerais de Início</label>
            <textarea class="form-control" id="inp-notes" rows="3" placeholder="Condições climáticas, autorizações de trabalho (PT), isolamento...">${this.escapeHtml(this.notes)}</textarea>
          </div>
        </div>
      </div>
    `;
  }

  private renderStepChecklist(): string {
    const rejectedCount = this.checklistItems.filter(i => i.result === 'REJECTED').length;
    const approvedCount = this.checklistItems.filter(i => i.result === 'APPROVED').length;

    return `
      <div>
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; flex-wrap: wrap; gap: 8px;">
          <div>
            <h3 style="color: var(--color-text-primary); margin: 0;">2. Checklist de Inspeção Visual</h3>
            <p style="color: var(--color-text-muted); font-size: 0.875rem; margin: 4px 0 0 0;">
              Avalie cada componente estrutural. Itens rejeitados exigem descrição mandatória do defeito e fotos.
            </p>
          </div>
          <div style="display: flex; gap: 8px;">
            <span style="background: rgba(34,197,94,0.15); color: var(--status-success-border); border: 1px solid rgba(34,197,94,0.3); padding: 4px 10px; border-radius: 4px; font-size: 0.8rem; font-weight: 600;">
              ${approvedCount} Aprovados
            </span>
            <span style="background: rgba(239,68,68,0.15); color: var(--status-danger-border); border: 1px solid rgba(239,68,68,0.3); padding: 4px 10px; border-radius: 4px; font-size: 0.8rem; font-weight: 600;">
              ${rejectedCount} Rejeitados
            </span>
          </div>
        </div>

        <div id="checklist-list">
          ${this.checklistItems.map((item, idx) => `
            <div class="checklist-row" data-item-idx="${idx}">
              <div class="checklist-header-row">
                <div>
                  <span style="font-size: 0.75rem; color: var(--color-brand-cyan); font-weight: 600; text-transform: uppercase;">${item.groupId}</span>
                  <div style="color: var(--color-text-primary); font-weight: 500; font-size: 0.95rem;">${item.name}</div>
                </div>
                <div class="pill-group">
                  <button type="button" class="result-btn ${item.result === 'APPROVED' ? 'active-approved' : ''}" data-res="APPROVED">
                    APROVADO
                  </button>
                  <button type="button" class="result-btn ${item.result === 'REJECTED' ? 'active-rejected' : ''}" data-res="REJECTED">
                    REJEITADO
                  </button>
                  <button type="button" class="result-btn ${item.result === 'NOT_APPLICABLE' ? 'active-na' : ''}" data-res="NOT_APPLICABLE">
                    N/A
                  </button>
                </div>
              </div>

              ${item.result === 'REJECTED' ? `
                <div class="defect-box">
                  <div class="form-group">
                    <label class="form-label" style="color: var(--status-danger-border);">Descrição Obrigatória da Anomalia / Defeito *</label>
                    <textarea class="form-control inp-defect-desc" rows="2" placeholder="Descreva localização, extensão e características do dano...">${this.escapeHtml(item.description)}</textarea>
                  </div>
                  <div class="form-group">
                    <label class="form-label">Notas e Ações Recomendadas</label>
                    <input class="form-control inp-defect-notes" placeholder="Ex: Medir espessura por ultrassom..." value="${this.escapeHtml(item.inspectionNotes)}">
                  </div>
                  <div style="display: flex; gap: 8px; align-items: center; margin-top: 4px;">
                    <app-button variant="secondary" size="sm" class="btn-add-mock-photo">
                      <i data-lucide="camera" style="width: 14px; height: 14px; margin-right: 4px;"></i> Anexar Foto Evidência
                    </app-button>
                    <span style="font-size: 0.75rem; color: var(--color-text-muted);">
                      ${item.photos.length > 0 ? `${item.photos.length} foto(s) anexada(s)` : 'Sem fotos'}
                    </span>
                  </div>
                </div>
              ` : `
                <div style="font-size: 0.8rem; color: var(--color-text-muted);">
                  ${item.inspectionNotes ? `Nota: ${item.inspectionNotes}` : 'Sem observações'}
                </div>
              `}
            </div>
          `).join('')}
        </div>

        <div style="margin-top: 16px; text-align: center;">
          <app-button variant="outline" size="sm" id="btn-add-manual-component">
            <i data-lucide="plus" style="width: 16px; height: 16px; margin-right: 6px;"></i> Adicionar Componente Manualmente
          </app-button>
        </div>
      </div>
    `;
  }

  private renderStepNdt(): string {
    return `
      <div>
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; flex-wrap: wrap; gap: 8px;">
          <div>
            <h3 style="color: var(--color-text-primary); margin: 0;">3. Ensaios Não Destrutivos (END)</h3>
            <p style="color: var(--color-text-muted); font-size: 0.875rem; margin: 4px 0 0 0;">
              Registre os ensaios realizados (Medição de Espessura ME, Líquido Penetrante LP, Partículas Magnéticas PM, Ultrassom).
            </p>
          </div>
          <div style="display: flex; gap: 8px;">
            <app-button variant="secondary" size="sm" id="btn-add-me">
              <i data-lucide="plus" style="width: 14px; height: 14px; margin-right: 4px;"></i> + ME (Espessura)
            </app-button>
            <app-button variant="secondary" size="sm" id="btn-add-lp">
              <i data-lucide="plus" style="width: 14px; height: 14px; margin-right: 4px;"></i> + LP (Penetrante)
            </app-button>
          </div>
        </div>

        <div style="display: flex; flex-direction: column; gap: 12px;">
          ${this.ndtRecords.map((ndt, idx) => `
            <div style="background: var(--color-background); border: 1px solid var(--color-border-default); border-radius: 8px; padding: 16px; display: flex; justify-content: space-between; align-items: center;">
              <div>
                <div style="display: flex; gap: 8px; align-items: center; margin-bottom: 4px;">
                  <span style="background: rgba(59,130,246,0.2); color: var(--ia-info-fg, #60a5fa); padding: 2px 8px; border-radius: 4px; font-size: 0.75rem; font-weight: 700;">
                    ${ndt.type}
                  </span>
                  <strong style="color: var(--color-text-primary); font-size: 0.95rem;">${ndt.component}</strong>
                  <span style="color: var(--color-text-muted); font-size: 0.8rem;">(${ndt.standard})</span>
                </div>
                <div style="color: var(--color-text-secondary); font-size: 0.85rem;">${ndt.findings}</div>
                ${ndt.assetNames && ndt.assetNames.length > 0 ? `<div style="color: var(--ia-info-fg, #60a5fa); font-size: 0.75rem; margin-top: 4px;">Equipamentos vinculados: ${ndt.assetNames.join(', ')}</div>` : ''}
              </div>
              <div style="display: flex; align-items: center; gap: 12px;">
                <span style="background: ${ndt.result === 'APPROVED' ? 'rgba(34,197,94,0.2)' : 'rgba(239,68,68,0.2)'}; color: ${ndt.result === 'APPROVED' ? 'var(--status-success-border)' : 'var(--status-danger-border)'}; padding: 4px 10px; border-radius: 4px; font-size: 0.75rem; font-weight: 600;">
                  ${ndt.result === 'APPROVED' ? 'APROVADO' : 'REPROVADO'}
                </span>
                <button type="button" class="btn-remove-ndt" data-idx="${idx}" style="background: none; border: none; color: var(--status-danger-solid); cursor: pointer;">
                  <i data-lucide="trash-2" style="width: 16px; height: 16px;"></i>
                </button>
              </div>
            </div>
          `).join('')}

          ${this.ndtRecords.length === 0 ? `
            <div style="padding: 32px; text-align: center; color: var(--color-text-muted); border: 1px dashed var(--color-border-default); border-radius: 8px;">
              Nenhum ensaio END cadastrado para esta inspeção.
            </div>
          ` : ''}
        </div>
      </div>
    `;
  }

  private renderStepAvr(): string {
    const { previousThickness, currentThickness, minimumThickness, intervalYears } = this.avrData;
    const corrosionRate = intervalYears > 0 ? (previousThickness - currentThickness) / intervalYears : 0;
    const remainingLife = corrosionRate > 0 ? (currentThickness - minimumThickness) / corrosionRate : 99;
    const criticality = determineAvrCriticality(currentThickness, minimumThickness, remainingLife);

    let critColor = 'var(--status-success-solid)';
    if (criticality === 'CRITICAL') critColor = 'var(--status-danger-solid)';
    else if (criticality === 'HIGH') critColor = '#f97316';
    else if (criticality === 'MODERATE') critColor = 'var(--status-warning-solid)';

    return `
      <div>
        <h3 style="color: var(--color-text-primary); margin-top: 0; margin-bottom: 8px;">4. Avaliação de Vida Remanescente (AVR / NR-13)</h3>
        <p style="color: var(--color-text-muted); font-size: 0.875rem; margin-bottom: 24px;">
          Cálculo automatizado da taxa de corrosão ($TC$) e estimativa de vida remanescente ($VR$) com base nas medições de ultrassom.
        </p>

        <div class="form-grid">
          <div class="form-group">
            <label class="form-label">Espessura Anterior (e_anterior) [mm] *</label>
            <input type="number" step="0.1" class="form-control" id="inp-avr-e-ant" value="${previousThickness}">
          </div>

          <div class="form-group">
            <label class="form-label">Espessura Atual Medida (e_atual) [mm] *</label>
            <input type="number" step="0.1" class="form-control" id="inp-avr-e-atual" value="${currentThickness}">
          </div>

          <div class="form-group">
            <label class="form-label">Espessura Mínima de Projeto (e_min) [mm] *</label>
            <input type="number" step="0.1" class="form-control" id="inp-avr-e-min" value="${minimumThickness}">
          </div>

          <div class="form-group">
            <label class="form-label">Intervalo entre Medições (t) [Anos] *</label>
            <input type="number" step="0.5" class="form-control" id="inp-avr-interval" value="${intervalYears}">
          </div>
        </div>

        <div class="kpi-grid">
          <div class="kpi-box">
            <span class="kpi-title">Taxa de Corrosão (TC)</span>
            <div class="kpi-val" style="color: var(--ia-info-fg, #60a5fa);">${corrosionRate.toFixed(3)}</div>
            <span class="kpi-unit">mm / ano</span>
          </div>

          <div class="kpi-box">
            <span class="kpi-title">Vida Remanescente (VR)</span>
            <div class="kpi-val" style="color: ${critColor};">${remainingLife > 50 ? '> 50' : remainingLife.toFixed(1)}</div>
            <span class="kpi-unit">anos</span>
          </div>

          <div class="kpi-box">
            <span class="kpi-title">Criticidade Estrutural</span>
            <div class="kpi-val" style="color: ${critColor}; font-size: 1.2rem; margin-top: 6px;">
              ${criticality}
            </div>
            <span class="kpi-unit">Conforme Norma NR-13</span>
          </div>

          <div class="kpi-box">
            <span class="kpi-title">Próxima Inspeção Recomendada</span>
            <div class="kpi-val" style="color: var(--color-text-primary); font-size: 1.2rem; margin-top: 6px;">
              ${Math.min(remainingLife / 2, 5).toFixed(1)} anos
            </div>
            <span class="kpi-unit">Limite máximo: 5 anos</span>
          </div>
        </div>

        <div style="margin-top: 20px; background: rgba(59,130,246,0.08); border: 1px solid rgba(59,130,246,0.3); border-radius: 8px; padding: 16px;">
          <strong style="color: var(--color-border-default); font-size: 0.9rem;">Fórmula Aplicada:</strong>
          <div style="font-family: monospace; color: var(--color-text-primary); margin-top: 4px; font-size: 0.85rem;">
            TC = (e_ant - e_atual) / Δt = (${previousThickness} - ${currentThickness}) / ${intervalYears} = ${corrosionRate.toFixed(3)} mm/ano<br>
            VR = (e_atual - e_min) / TC = (${currentThickness} - ${minimumThickness}) / ${corrosionRate.toFixed(3)} = ${remainingLife.toFixed(1)} anos
          </div>
        </div>
      </div>
    `;
  }

  private renderStepRecommendations(): string {
    return `
      <div>
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; flex-wrap: wrap; gap: 8px;">
          <div>
            <h3 style="color: var(--color-text-primary); margin: 0;">5. Recomendações de Inspeção</h3>
            <p style="color: var(--color-text-muted); font-size: 0.875rem; margin: 4px 0 0 0;">
              Recomendações técnicas vinculadas aos defeitos identificados nas etapas anteriores.
            </p>
          </div>
          <app-button variant="secondary" size="sm" id="btn-add-quick-rec">
            <i data-lucide="plus" style="width: 14px; height: 14px; margin-right: 4px;"></i> Nova Recomendação
          </app-button>
        </div>

        <div style="display: flex; flex-direction: column; gap: 12px;">
          ${this.recommendations.map((rec, idx) => `
            <div style="background: var(--color-background); border: 1px solid var(--color-border-default); border-radius: 8px; padding: 16px;">
              <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
                <div style="display: flex; gap: 6px; align-items: center;">
                  <span style="background: rgba(59,130,246,0.2); color: var(--ia-info-fg, #60a5fa); padding: 2px 8px; border-radius: 4px; font-size: 0.75rem; font-weight: 700;">
                    ${rec.discipline}
                  </span>
                  <span style="background: ${rec.criticality === 'CRÍTICA' ? 'rgba(239,68,68,0.2)' : 'rgba(249,115,22,0.2)'}; color: ${rec.criticality === 'CRÍTICA' ? 'var(--status-danger-border)' : '#fdba74'}; padding: 2px 8px; border-radius: 4px; font-size: 0.75rem; font-weight: 700;">
                    ${rec.criticality}
                  </span>
                  <span style="background: rgba(34,197,94,0.2); color: var(--status-success-border); padding: 2px 8px; border-radius: 4px; font-size: 0.75rem; font-weight: 700;">
                    ${rec.executeIn}
                  </span>
                  ${rec.zr ? `<span style="color: var(--color-text-muted); font-size: 0.75rem;">ZR: ${rec.zr}</span>` : ''}
                </div>
                <button type="button" class="btn-remove-rec" data-idx="${idx}" style="background: none; border: none; color: var(--status-danger-solid); cursor: pointer;">
                  <i data-lucide="trash-2" style="width: 16px; height: 16px;"></i>
                </button>
              </div>
              <div style="color: var(--color-text-primary); font-size: 0.9rem; margin-bottom: 4px;">${rec.scope}</div>
              ${rec.origin ? `<div style="color: var(--color-text-muted); font-size: 0.75rem;">Origem da Evidência: ${rec.origin}</div>` : ''}
            </div>
          `).join('')}
        </div>
      </div>
    `;
  }

  private renderStepRiSummary(): string {
    const rejectedItems = this.checklistItems.filter(i => i.result === 'REJECTED');
    const approvedItems = this.checklistItems.filter(i => i.result === 'APPROVED');

    return `
      <div>
        <h3 style="color: var(--color-text-primary); margin-top: 0; margin-bottom: 8px;">6. Emissão do Relatório de Integridade (RI)</h3>
        <p style="color: var(--color-text-muted); font-size: 0.875rem; margin-bottom: 24px;">
          Revise a consolidação de todos os dados coletados antes de gerar o documento formal de 14 seções.
        </p>

        <div class="kpi-grid" style="margin-bottom: 24px;">
          <div class="kpi-box">
            <span class="kpi-title">Equipamento Selecionado</span>
            <div class="kpi-val" style="font-size: 1.2rem; color: var(--ia-info-fg, #60a5fa);">${this.escapeHtml(this.equipments.find(e => e.id === this.selectedEquipmentId)?.tag || 'Nenhum')}</div>
            <span class="kpi-unit">Tipo: ${this.inspectionType}</span>
          </div>

          <div class="kpi-box">
            <span class="kpi-title">Checklist Visual</span>
            <div class="kpi-val" style="font-size: 1.2rem; color: ${rejectedItems.length > 0 ? 'var(--status-danger-solid)' : '#4ade80'};">
              ${approvedItems.length} OK / ${rejectedItems.length} Anomalias
            </div>
            <span class="kpi-unit">Total: ${this.checklistItems.length} itens</span>
          </div>

          <div class="kpi-box">
            <span class="kpi-title">Ensaios Não Destrutivos</span>
            <div class="kpi-val" style="font-size: 1.2rem; color: var(--color-text-primary);">${this.ndtRecords.length} Executados</div>
            <span class="kpi-unit">ME, LP e Ultrassom</span>
          </div>

          <div class="kpi-box">
            <span class="kpi-title">Recomendações Emitidas</span>
            <div class="kpi-val" style="font-size: 1.2rem; color: var(--status-warning-text);">${this.recommendations.length} Itens</div>
            <span class="kpi-unit">Para plano de ação</span>
          </div>
        </div>

        <div style="background: var(--color-surface); border: 1px solid var(--color-border-default); border-radius: 8px; padding: 20px; text-align: center;">
          <i data-lucide="shield-check" style="width: 48px; height: 48px; color: var(--ia-primary); margin-bottom: 12px;"></i>
          <h4 style="color: var(--color-text-primary); margin: 0 0 8px 0; font-size: 1.1rem;">Pronto para Consolidar o RI</h4>
          <p style="color: var(--color-text-muted); font-size: 0.875rem; max-width: 600px; margin: 0 auto 20px auto;">
            Ao clicar abaixo, o sistema salvará a inspeção no banco de dados e instanciará o Relatório de Integridade com todas as 14 seções populadas (Objetivo, Snapshot, Visual, END, Manutenção, AVR, Recomendações e Signatários).
          </p>
          <app-button variant="primary" size="md" id="btn-create-ri-final">
            <i data-lucide="file-text" style="width: 16px; height: 16px; margin-right: 6px;"></i> Gerar e Abrir Relatório de Integridade (RI)
          </app-button>
        </div>
      </div>
    `;
  }

  private attachEvents() {
    this.querySelector('#btn-back-inspections')?.addEventListener('click', () => {
      router.navigate('/inspections');
    });

    // Step navigation pills
    this.querySelectorAll('[data-step-idx]').forEach(el => {
      el.addEventListener('click', (e) => {
        const idx = parseInt((e.currentTarget as HTMLElement).dataset['stepIdx'] || '0');
        this.saveCurrentStepData();
        this.currentStepIndex = idx;
        this.render();
      });
    });

    // Step footer buttons
    this.querySelector('#btn-prev-step')?.addEventListener('click', () => {
      if (this.currentStepIndex > 0) {
        this.saveCurrentStepData();
        this.currentStepIndex--;
        this.render();
      }
    });

    this.querySelector('#btn-next-step')?.addEventListener('click', async () => {
      this.saveCurrentStepData();
      if (this.currentStepIndex === 0) {
        await this.persistInspectionStep1();
      }
      if (this.currentStepIndex < this.steps.length - 1) {
        this.currentStepIndex++;
        this.render();
      }
    });

    this.querySelector('#btn-save-draft')?.addEventListener('click', async () => {
      this.saveCurrentStepData();
      await this.persistInspectionStep1();
      alert("Dados da inspeção salvos com sucesso!");
    });

    // Step 0 events
    const selectEq = this.querySelector('#inp-eq-select') as HTMLSelectElement;
    selectEq?.addEventListener('change', () => {
      this.selectedEquipmentId = selectEq.value;
    });

    const selectNature = this.querySelector('#inp-nature') as HTMLSelectElement;
    selectNature?.addEventListener('change', () => {
      this.nature = selectNature.value as any;
      const groupReason = this.querySelector('#group-extraordinary-reason') as HTMLElement;
      if (groupReason) {
        groupReason.style.display = this.nature === 'EXTRAORDINARY' ? 'flex' : 'none';
      }
    });

    const selectInsp = this.querySelector('#inp-inspector') as HTMLSelectElement;
    selectInsp?.addEventListener('change', () => {
      this.inspectorId = selectInsp.value;
      const prof = this.professionals.find(p => p.id === this.inspectorId);
      const fnInput = this.querySelector('#inp-inspector-function') as HTMLInputElement;
      if (fnInput) {
        fnInput.value = prof?.functionTitle || 'INSPETOR DE EQUIPAMENTOS';
      }
    });

    // Step 1 events (Checklist result toggles)
    this.querySelectorAll('.result-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const target = e.currentTarget as HTMLElement;
        const row = target.closest('.checklist-row') as HTMLElement;
        if (!row) return;
        const idx = parseInt(row.dataset['itemIdx'] || '0');
        const res = target.dataset['res'] as any;
        if (this.checklistItems[idx]) {
          this.checklistItems[idx].result = res;
        }
        this.render();
      });
    });

    // Step 1 Defect description changes
    this.querySelectorAll('.inp-defect-desc').forEach((ta, i) => {
      ta.addEventListener('input', (e) => {
        const row = (e.target as HTMLElement).closest('.checklist-row') as HTMLElement;
        if (row) {
          const idx = parseInt(row.dataset['itemIdx'] || '0');
          if (this.checklistItems[idx]) {
            this.checklistItems[idx].description = (e.target as HTMLTextAreaElement).value;
          }
        }
      });
    });

    // Step 1 photo simulation
      this.querySelectorAll('.btn-add-mock-photo').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          const row = (e.currentTarget as HTMLElement).closest('.checklist-row') as HTMLElement;
          if (row) {
            const idx = parseInt(row.dataset['itemIdx'] || '0');
            if (this.checklistItems[idx]) {
              this.checklistItems[idx].photos.push(`/mock/photo_${Date.now()}.jpg`);
              alert("Foto anexada à evidência!");
            }
            this.render();
          }
        });
      });

      const btnAddManual = this.querySelector('#btn-add-manual-component');
      btnAddManual?.addEventListener('click', () => {
        const compName = prompt('Informe o nome do componente a ser adicionado:');
        if (compName && compName.trim()) {
          this.checklistItems.push({
            groupId: "ADICIONAL",
            definitionComponentId: `DEF-ADD-${Date.now()}`,
            name: compName.trim(),
            result: 'APPROVED',
            description: "",
            inspectionNotes: "",
            photos: []
          });
          this.render();
        }
      });

      // Step 2 events (END)
    this.querySelector('#btn-add-me')?.addEventListener('click', async () => {
      const comp = prompt("Informe o componente para Medição de Espessura (ME):", "Costado / Região Inferior");
      if (comp) {
        const val = prompt("Informe a espessura média medida (mm):", "10.2");
        // Optional link with NDT Asset
        const assetSnapshotIds: string[] = [];
        const assetNames: string[] = [];
        
        const linkAsset = async (type: any, label: string) => {
          if (confirm(`Deseja vincular um ${label}?`)) {
            try {
              const ctx = { activeClientId: contextStore.get().activeClientId, userId: contextStore.get().userId, userRole: contextStore.get().role, permissions: contextStore.get().permissions } as any;
              const assets = await window.app.platform.ndtAssetService.listAssets(ctx, type);
              if (assets.length > 0) {
                const options = assets.map((a: any, i: number) => `${i + 1} - ${a.internalCode} (${a.manufacturer || 'N/A'})`).join('\\n');
                const choice = prompt(`Selecione o ${label} (digite o número):\\n${options}`);
                if (choice) {
                  const idx = parseInt(choice) - 1;
                  if (assets[idx]) {
                    const asset = assets[idx];
                    const validation = await window.app.platform.ndtAssetService.validateAssetUsage(ctx, asset.id);
                    if (!validation.allowed && !validation.requiresOverride) {
                      alert('Este equipamento está inativo ou bloqueado para uso.');
                      return;
                    }
                    if (validation.requiresOverride) {
                      const reason = prompt('O certificado do equipamento está vencido. Informe a justificativa de override para liberar o uso:');
                      if (!reason) {
                        alert('Uso cancelado.');
                        return;
                      }
                      await window.app.platform.ndtAssetService.saveOverrideLog(ctx, {
                        id: String(Date.now()),
                        clientId: ctx.activeClientId,
                        assetId: asset.id,
                        reportId: 'TEMP',
                        professionalId: this.inspectorId,
                        userId: ctx.userId,
                        date: new Date().toISOString(),
                        reason
                      });
                    }
                    const snapshot = await window.app.platform.ndtAssetService.createSnapshot(ctx, asset.id);
                    assetSnapshotIds.push(snapshot.id);
                    assetNames.push(asset.internalCode);
                  }
                }
              } else {
                alert(`Nenhum ${label} cadastrado e ativo no sistema.`);
              }
            } catch (e: any) {
              console.error(e);
              alert("Erro ao buscar equipamentos: " + e.message);
            }
          }
        };

        await linkAsset('ULTRASONIC_THICKNESS_GAUGE', 'Aparelho de ME-US');
        await linkAsset('ULTRASONIC_TRANSDUCER', 'Transdutor');
        await linkAsset('CALIBRATION_BLOCK', 'Bloco de Calibração');
        
        const payload: any = {
          id: String(Date.now()),
          type: "ME",
          component: comp,
          standard: "ABNT NBR 16049",
          result: "APPROVED",
          findings: `Espessura de ${val || '10.2'} mm constatada.`,
          value: parseFloat(val || '10.2')
        };
        if (assetSnapshotIds.length > 0) payload.assetSnapshotIds = assetSnapshotIds;
        if (assetNames.length > 0) payload.assetNames = assetNames;
        
        this.ndtRecords.push(payload);
        this.render();
      }
    });

    this.querySelector('#btn-add-lp')?.addEventListener('click', () => {
      const comp = prompt("Informe o componente para Líquido Penetrante (LP):", "Solda do Bocal N2");
      if (comp) {
        this.ndtRecords.push({
          id: String(Date.now()),
          type: "LP",
          component: comp,
          standard: "ASME Sec. V Art. 6",
          result: "APPROVED",
          findings: "Superfície examinada sem trincas ou descontinuidades relevantes."
        });
        this.render();
      }
    });

    this.querySelectorAll('.btn-remove-ndt').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const idx = parseInt((e.currentTarget as HTMLElement).dataset['idx'] || '0');
        this.ndtRecords.splice(idx, 1);
        this.render();
      });
    });

    // Step 3 events (AVR inputs live calculation)
    const avrInputs = ['#inp-avr-e-ant', '#inp-avr-e-atual', '#inp-avr-e-min', '#inp-avr-interval'];
    avrInputs.forEach(id => {
      this.querySelector(id)?.addEventListener('input', () => {
        this.avrData.previousThickness = parseFloat((this.querySelector('#inp-avr-e-ant') as HTMLInputElement)?.value || '12.0');
        this.avrData.currentThickness = parseFloat((this.querySelector('#inp-avr-e-atual') as HTMLInputElement)?.value || '10.5');
        this.avrData.minimumThickness = parseFloat((this.querySelector('#inp-avr-e-min') as HTMLInputElement)?.value || '9.0');
        this.avrData.intervalYears = parseFloat((this.querySelector('#inp-avr-interval') as HTMLInputElement)?.value || '2.0');
        this.render();
      });
    });

    // Step 4 events (Recommendations)
    this.querySelector('#btn-add-quick-rec')?.addEventListener('click', () => {
      const scope = prompt("Informe o escopo da recomendação:");
      if (scope) {
        this.recommendations.push({
          id: `REC-${Date.now()}`,
          discipline: "MECNICA",
          criticality: "MODERADA",
          executeIn: "ROTINA",
          scope,
          zr: "ZR-01"
        });
        this.render();
      }
    });

    this.querySelectorAll('.btn-remove-rec').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const idx = parseInt((e.currentTarget as HTMLElement).dataset['idx'] || '0');
        this.recommendations.splice(idx, 1);
        this.render();
      });
    });

    // Step 5 Finish / Create RI
    const finishBtn = this.querySelector('#btn-finish-ri') || this.querySelector('#btn-create-ri-final');
    finishBtn?.addEventListener('click', async () => {
      await this.generateAndOpenReport();
    });
  }

  private saveCurrentStepData() {
    if (this.currentStepIndex === 0) {
      const selectEq = this.querySelector('#inp-eq-select') as HTMLSelectElement;
      if (selectEq) {
        if (this.selectedEquipmentId !== selectEq.value) {
          this.selectedEquipmentId = selectEq.value;
          this.updateChecklistItemsForEquipment(this.selectedEquipmentId);
        }
      }
      const selectType = this.querySelector('#inp-insp-type') as HTMLSelectElement;
      if (selectType) this.inspectionType = selectType.value;
      const selectNature = this.querySelector('#inp-nature') as HTMLSelectElement;
      if (selectNature) this.nature = selectNature.value as any;
      const inpExtra = this.querySelector('#inp-extra-reason') as HTMLInputElement;
      if (inpExtra) this.extraordinaryReason = inpExtra.value;
      const inpInsp = this.querySelector('#inp-inspector') as HTMLInputElement;
      if (inpInsp) this.inspectorId = inpInsp.value;
      const inpNotes = this.querySelector('#inp-notes') as HTMLTextAreaElement;
      if (inpNotes) this.notes = inpNotes.value;
    }
  }

  private updateChecklistItemsForEquipment(equipmentId: string) {
    const eq = this.equipments.find(e => e.id === equipmentId);
    if (!eq) return;

    if (eq.equipmentType === 'PIPING_SYSTEM' || eq.equipmentType === 'PIPING_LINE') {
      const pipingItems = [
        "TUBOS", "CONEXÕES", "ESTOJOS/PORCAS", "ISOLAMENTO TÉRMICO", 
        "PINTURA", "ACESSÓRIOS", "SUPORTES", "ATERRAMENTO", 
        "DISPOSITIVOS DE SEGURANÇA", "INSTRUMENTOS", "IDENTIFICAÇÃO"
      ];
      this.checklistItems = pipingItems.map((name, i) => ({
        groupId: "TUBULAÇÃO", definitionComponentId: `DEF-PIP-${i+1}`, name, result: 'APPROVED', description: "", inspectionNotes: "", photos: []
      }));
    } else {
      const vesselItems = [
        { groupId: "CASCO", name: "Costado / Chapa do Casco" },
        { groupId: "TAMPOS", name: "Tampo Superior / Cabeçote" },
        { groupId: "TAMPOS", name: "Tampo Inferior" },
        { groupId: "BOCAIS", name: "Bocal de Entrada de Produto" },
        { groupId: "BOCAIS", name: "Bocal de Saída de Produto" },
        { groupId: "VALVULAS", name: "Válvula de Segurança" },
        { groupId: "SUPORTES", name: "Berço / Sapatas de Apoio" },
        { groupId: "PINTURA", name: "Revestimento / Pintura" },
        { groupId: "INSTRUMENTOS", name: "Manômetro / Instrumentos" }
      ];
      this.checklistItems = vesselItems.map((item, i) => ({
        groupId: item.groupId, definitionComponentId: `DEF-VES-${i+1}`, name: item.name, result: 'APPROVED', description: "", inspectionNotes: "", photos: []
      }));
    }
  }

  private async persistInspectionStep1() {
    try {
      const p = window.app.platform;
      const ctx = sessionRequestContext();

      if (!this.activeInspectionId) {
        const startInput: Parameters<typeof p.inspectionService.startInspection>[1] = {
          equipmentId: this.selectedEquipmentId as EquipmentId,
          inspectionType: this.inspectionType,
          modality: this.modality,
          nature: this.nature,
          inspectorId: this.inspectorId as ProfessionalId,
          notes: this.notes
        };
        if (this.nature === 'EXTRAORDINARY' && this.extraordinaryReason) {
          startInput.extraordinaryReason = this.extraordinaryReason;
        }
        const { inspection, checklist } = await p.inspectionService.startInspection(ctx, startInput);
        this.activeInspectionId = inspection.id;
        this.activeChecklistId = checklist.id;
      }
    } catch (e: any) {
      console.warn("Error persisting inspection step 1", e);
    }
  }

  private async generateAndOpenReport() {
    try {
      const p = window.app.platform;
      const ctx = sessionRequestContext();

      // Ensure inspection exists
      if (!this.activeInspectionId) {
        await this.persistInspectionStep1();
      }

      const inspId = this.activeInspectionId || domainId<InspectionId>("11111111-1111-4111-a111-111111111111");

      // Check if report exists or create new
      let report = await p.reportService.getReportByInspection(ctx, inspId);
      if (!report) {
        report = await p.reportService.createDraftReport(ctx, inspId);
      }

      // Add all recommendations to the report
      for (const rec of this.recommendations) {
        try {
          const recPayload: any = {
            discipline: rec.discipline,
            criticality: rec.criticality as RecommendationCriticality,
            executeIn: rec.executeIn as RecommendationExecuteIn,
            scope: rec.scope
          };
          if (rec.zr) recPayload.zr = rec.zr;
          await p.reportService.addRecommendation(ctx, report.id, recPayload);
        } catch {
          // Ignore duplicates
        }
      }

      router.navigate(`/reports/integrity/edit/${report.id}`);
    } catch (e: any) {
      console.error(e);
      alert("Erro ao consolidar RI: " + e.message);
    }
  }
}

if (!customElements.get("app-inspection-flow")) {
  customElements.define("app-inspection-flow", AppInspectionFlow);
}


